import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { v, num, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartySlot } from '../../../parties/index.js';
export const metadata = {
    // Document sections in fixed order (per ADR-0049). § numbers are
    // computed from visibility; refs are the stable identity. Field-less
    // sections (static text / signing / bilagor) are declared so they
    // get numbered even though they carry no editor field.
    sections: [
        { ref: 'hyresvard', title: 'Uppgifter om hyresvärden' },
        { ref: 'hyresobjekt', title: 'Uppgifter om hyresobjekt' },
        { ref: 'hyresgast', title: 'Uppgifter om hyresgästen' },
        { ref: 'information', title: 'Information om planerade åtgärder' },
    ],
    templateKey: 'meddelande_18e',
    version: 'v1',
    category: 'Ombyggnad och ändring',
    title: 'Meddelande till hyresgäst enligt 12 kap. 18 e § jordabalken',
    shortTitle: 'Meddelande 18 e §',
    titleSuffix: { key: 'apartment_official_number', prefix: 'lgh' },
    formNumber: 'Formulär R-06',
    preamble: 'Hyresvärden informerar härmed hyresgästen om planerade ombyggnads- och ändringsåtgärder.',
    description: 'Förhandsmeddelande från hyresvärden till hyresgästen inför planerade ombyggnads- eller ändringsåtgärder enligt 12 kap. 18 e § jordabalken.\n\n' +
        'Vem undertecknar: endast hyresvärden.\n' +
        'Leverans: rekommenderad post till hyresgästen — går inte via Scrive.\n\n' +
        'Detta meddelande är "huvudavtalet" i renoveringsärendet. Skapa det först, lägg sedan till ett 18 d Godkännande som kopplad mall — godkännandet bifogas meddelandet vid utskick. När hyresgästen inte godkänner åtgärderna kan ärendet hänskjutas till hyresnämnden.',
    status: 'active',
    signingMode: 'manual_delivery',
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            signingLabel: 'Hyresvärdens namnteckning',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee',
            label: 'Hyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: false,
            help: 'Hyresgästen är mottagare av meddelandet — undertecknar inte.',
        },
    ],
    // Party projection (ADR-0065) — editor fields; the body renders parties
    // across separate sections, so it is not PartyBlock-driven. Spread per
    // slot (expandPartySlot) since the blocks are non-contiguous.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'hyresvard',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'landlord_address', requiredAtFinal: true },
                { attr: 'email', key: 'landlord_email' },
                { attr: 'phone', key: 'landlord_phone' },
            ],
        },
        {
            slot: 'lessee',
            section: 'hyresgast',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_personnummer', help: 'PII — ej sökbart' },
                { attr: 'email', key: 'tenant_email' },
                { attr: 'phone', key: 'tenant_phone' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
};
export const variableSchema = [
    // Hyresvärd — generated from metadata.partyProjection (ADR-0065).
    ...expandPartySlot(metadata, 'lessor'),
    // Hyresobjekt
    {
        key: 'apartment_official_number',
        label: 'Lägenhetsnummer',
        type: 'text',
        section: 'hyresobjekt',
        help: 'T.ex. det officiella lägenhetsnumret (1101 etc.)',
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'apartment_rooms',
        label: 'Antal rum och kök',
        type: 'select',
        section: 'hyresobjekt',
        options: [
            { value: '1 rum och kokskåp', label: '1 rum och kokskåp', code: 'r1_kokskap', summary: '1 room with a kitchenette cupboard.' },
            { value: '1 rum och kokvrå', label: '1 rum och kokvrå', code: 'r1_kokvra', summary: '1 room with a kitchen nook.' },
            { value: '1 rum och kök', label: '1 rum och kök', code: 'r1_kok', summary: '1 room with a full kitchen.' },
            { value: '1,5 rum och kök', label: '1,5 rum och kök', code: 'r1_5_kok', summary: '1.5 rooms with a full kitchen.' },
            { value: '2 rum och kokvrå', label: '2 rum och kokvrå', code: 'r2_kokvra', summary: '2 rooms with a kitchen nook.' },
            { value: '2 rum och kök', label: '2 rum och kök', code: 'r2_kok', summary: '2 rooms with a full kitchen.' },
            { value: '2,5 rum och kök', label: '2,5 rum och kök', code: 'r2_5_kok', summary: '2.5 rooms with a full kitchen.' },
            { value: '3 rum och kök', label: '3 rum och kök', code: 'r3_kok', summary: '3 rooms with a full kitchen.' },
            { value: '3,5 rum och kök', label: '3,5 rum och kök', code: 'r3_5_kok', summary: '3.5 rooms with a full kitchen.' },
            { value: '4 rum och kök', label: '4 rum och kök', code: 'r4_kok', summary: '4 rooms with a full kitchen.' },
            { value: '4,5 rum och kök', label: '4,5 rum och kök', code: 'r4_5_kok', summary: '4.5 rooms with a full kitchen.' },
            { value: '5 rum och kök', label: '5 rum och kök', code: 'r5_kok', summary: '5 rooms with a full kitchen.' },
            { value: '6 rum och kök', label: '6 rum och kök', code: 'r6_kok', summary: '6 rooms with a full kitchen.' },
            { value: '7 rum och kök', label: '7 rum och kök', code: 'r7_kok', summary: '7 rooms with a full kitchen.' },
            { value: '8 rum och kök', label: '8 rum och kök', code: 'r8_kok', summary: '8 rooms with a full kitchen.' },
        ],
    },
    {
        key: 'apartment_area_sqm',
        label: 'Yta i m²',
        type: 'number',
        min: 0,
        section: 'hyresobjekt',
    },
    {
        key: 'property_address',
        label: 'Adress (postnummer och kommun)',
        type: 'textarea',
        section: 'hyresobjekt',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'hyresobjekt',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    // Hyresgäst — generated from metadata.partyProjection (ADR-0065).
    ...expandPartySlot(metadata, 'lessee'),
];
export const sampleValues = {
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556677-8899',
    landlord_address: 'Sveavägen 100\n113 50 Stockholm',
    landlord_email: 'fastighet@bostadsagarna.se',
    landlord_phone: '08-123 45 67',
    apartment_official_number: '1101',
    apartment_rooms: '2 rum och kök',
    apartment_area_sqm: 58,
    property_address: 'Götgatan 35\n116 21 Stockholm',
    property_designation: 'Klockaren 5',
    tenant_name: 'Anna Andersson',
    tenant_personnummer: '19850412-1234',
    tenant_email: 'anna@example.com',
    tenant_phone: '070-123 45 67',
};
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ mode, values, signatories, }) {
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-4 italic text-stone-600", children: metadata.preamble }), _jsxs(Section, { sectionRef: "hyresvard", title: "Uppgifter om hyresv\u00E4rden", children: [_jsx(FieldRow, { label: "Namn", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Personnr / Org.nr", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Adress", children: v(values.landlord_address) })] }), _jsxs(Section, { sectionRef: "hyresobjekt", title: "Uppgifter om hyresobjekt", children: [_jsx(FieldRow, { label: "L\u00E4genhetsnummer", children: v(values.apartment_official_number) }), _jsx(FieldRow, { label: "Antal rum och k\u00F6k", children: v(values.apartment_rooms) }), _jsx(FieldRow, { label: "Yta i m\u00B2", children: num(values.apartment_area_sqm) }), _jsx(FieldRow, { label: "Adress (postnummer och kommun)", children: v(values.property_address) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) })] }), _jsxs(Section, { sectionRef: "hyresgast", title: "Uppgifter om hyresg\u00E4sten", children: [_jsx(FieldRow, { label: "Namn", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Personnr / Org.nr", children: v(values.tenant_personnummer) }), _jsx(FieldRow, { label: "E-post", children: v(values.tenant_email) }), _jsx(FieldRow, { label: "Telefonnummer", children: v(values.tenant_phone) })] }), _jsx(Section, { sectionRef: "information", title: "Information om planerade \u00E5tg\u00E4rder", children: _jsx("p", { className: "text-[10.5px] leading-relaxed", children: "Din hyresv\u00E4rd \u00F6nskar genomf\u00F6ra de \u00E5tg\u00E4rder som framg\u00E5r i bifogad godk\u00E4nnandeblankett. Vi ber dig godk\u00E4nna att \u00E5tg\u00E4rderna f\u00E5r genomf\u00F6ras. Det sker genom att underteckna godk\u00E4nnandet nedan. Om vi inte erh\u00E5ller godk\u00E4nnande kan hyresn\u00E4mnden komma att ge tillst\u00E5nd till \u00E5tg\u00E4rderna." }) }), _jsxs(KeepTogether, { children: [_jsx(SigningBlock, { parties: [
                            { label: 'Hyresvärdens namnteckning', role: 'lessor', ordinal: 0 },
                        ], signatories: signatories }), _jsxs("div", { className: "mt-3 text-[10.5px] leading-relaxed", children: [_jsx(FieldRow, { label: "Telefonnummer", children: v(values.landlord_phone) }), _jsx(FieldRow, { label: "E-post", children: v(values.landlord_email) })] }), _jsx("p", { className: "mt-4 text-[10.5px] italic text-stone-600", children: "Skickad till hyresg\u00E4st med rekommenderad post." })] })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map